import React, { useState, useMemo, useEffect } from 'react';
import { Download, Trash2 } from 'lucide-react';
import { trackTimeEntryDeleted, trackButtonClick } from '@/lib/posthog-events';
import { GenericTable, GenericTableColumn } from '@/styles/GenericTable';
import { NewItemButton, SearchButton, ThreeDotMenuButton } from '@/styles/StandardButtons';
import { TimeEntryForm } from './TimeEntryForm';
import { useProject } from '@/components/ProjectSelector';
import { useUser } from '@/contexts/UserContext';
import { formatDuration } from '@/lib/format-duration';
import { deleteTimeEntry, type TimeEntryRow } from '../api';
import { useEntityList } from '@/hooks/useEntityList';
import { fetchTeamMemberLookup } from '@/lib/entity-api';
import { exportEntityCsv } from '@/lib/csv';
import { useTimeTracker } from '../time-tracker-context';

const fullName = (person?: { first_name?: string | null; last_name?: string | null } | null) =>
  [person?.first_name, person?.last_name].filter(Boolean).join(' ').trim() || null;

/** A `time_entry` row from the API as the table shows it (names come joined; the duration is derived by the server). */
const toTimeEntryRow = (row: any): TimeEntryRow => ({
  id: row.id,
  project_id: row.project_id,
  project_name: row.project?.name ?? null,
  user_id: row.user_id,
  user_name: fullName(row.user),
  date: row.date,
  start_time: row.start_time,
  end_time: row.end_time,
  description: row.description || '',
  duration_seconds: row.duration_seconds ?? 0,
});

export function TimeEntriesTable() {
  const { selectedProject, projects: activeProjects } = useProject();
  const { user } = useUser();
  const { savedVersion } = useTimeTracker();
  const [showForm, setShowForm] = useState(false);
  const [editingEntry, setEditingEntry] = useState<TimeEntryRow | null>(null);
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  // The list follows the Global Project Selector (as it always has); search, sort and paging are the server's, over EVERY entry.
  const list = useEntityList<any, TimeEntryRow>({
    entityType: 'time_entry',
    projectId: selectedProject,
    enabled: !!user,
    mode: 'pages',
    defaultSort: { columnKey: 'date', direction: 'desc', sortKey: 'date' },
    mapRow: toTimeEntryRow,
  });

  // An entry the tracker just saved: show it.
  const { reload } = list;
  useEffect(() => { if (savedVersion > 0) reload(); }, [savedVersion, reload]);

  // A new query (project, search, sort) is a new list: what was selected belongs to the old one.
  useEffect(() => { setSelectedRows([]); }, [list.queryKey]);

  // The project dropdown of the form: the company's active projects, plus the entry's own project if it is no longer active.
  const formProjects = useMemo(() => {
    const options = activeProjects.map((p) => ({ id: p.id, name: p.name }));
    if (editingEntry && !options.some((p) => p.id === editingEntry.project_id)) options.unshift({ id: editingEntry.project_id, name: editingEntry.project_name || 'Unknown Project' });
    return options;
  }, [activeProjects, editingEntry]);

  const handleNewEntry = () => {
    trackButtonClick('new_time_entry_button', { context: 'time_tracking' });
    setEditingEntry(null);
    setShowForm(true);
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingEntry(null);
  };

  const handleDeleteSelected = async () => {
    if (selectedRows.length === 0) {
      alert('Please select at least one time entry to delete.');
      return;
    }
    if (!window.confirm(`Are you sure you want to delete ${selectedRows.length} time entry(s)? This action cannot be undone.`)) return;

    let deletedCount = 0;
    let failedCount = 0;
    for (const id of selectedRows) {
      try {
        await deleteTimeEntry(id);
        trackTimeEntryDeleted({ time_entry_id: id, deleted_by: user?.id });
        deletedCount++;
      } catch (err) {
        console.error('Error deleting time entry:', err);
        failedCount++;
      }
    }
    setSelectedRows([]);
    list.refresh();
    alert(failedCount > 0 ? `Deleted ${deletedCount} time entry(s). ${failedCount} failed.` : `Successfully deleted ${deletedCount} time entry(s).`);
  };

  // Export to CSV: the entries selected in the table, or -- with nothing selected -- EVERY entry of the current project +
  // search, read by the backend (not only the page on screen). Names are added from the company's people / projects.
  const handleExportSelected = () => exportEntityCsv(async (ids) => {
    const [exported, people] = await Promise.all([list.exportRows(ids), fetchTeamMemberLookup()]);
    const userNames = new Map(people.map((p) => [p.id, p.full_name] as const));
    const projectNames = new Map(activeProjects.map((p) => [p.id, p.name] as const));
    return exported.map((r) => ({
      id: r.id,
      user: userNames.get(r.user_id) ?? null,
      project: projectNames.get(r.project_id) ?? null,
      date: r.date,
      start_time: r.start_time,
      end_time: r.end_time,
      duration_seconds: r.duration_seconds,
      description: r.description,
    }));
  }, selectedRows, 'time_entries', 'time entries');

  const formatDate = (value: string | null, fallback: string) => new Date(value || fallback).toLocaleDateString();
  const formatTime = (value: string | null) => (value ? new Date(value).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '');

  const columns: GenericTableColumn<TimeEntryRow>[] = [
    { key: 'user_name', header: 'User', width: '10rem', sortable: true, sortKey: 'user', render: (row) => <span className="font-medium">{row.user_name || row.user_id}</span> },
    { key: 'project_name', header: 'Project', width: '12rem', sortable: true, sortKey: 'project', render: (row) => row.project_name || row.project_id },
    { key: 'duration', header: 'Duration', width: '6.5rem', render: (row) => formatDuration(row.duration_seconds) },
    { key: 'date', header: 'Date', width: '6.5rem', sortable: true, sortKey: 'date', render: (row) => formatDate(row.start_time, row.date) },
    { key: 'start_time', header: 'Start Time', width: '6.5rem', sortable: true, sortKey: 'start_time', render: (row) => formatTime(row.start_time) },
    { key: 'end_time', header: 'End Time', width: '6.5rem', sortable: true, sortKey: 'end_time', render: (row) => formatTime(row.end_time) },
  ];

  const menuItems = [
    { label: 'Export', icon: <Download className="w-4 h-4" />, onClick: handleExportSelected },
    { label: 'Delete', icon: <Trash2 className="w-4 h-4" />, onClick: handleDeleteSelected, destructive: true },
  ];

  return (
    <div className="space-y-6 w-full">
      <TimeEntryForm
        mode={editingEntry ? 'edit' : 'create'}
        initialData={editingEntry ? {
          id: editingEntry.id,
          project_id: editingEntry.project_id,
          start_time: editingEntry.start_time || undefined,
          duration_seconds: editingEntry.duration_seconds,
        } : null}
        projects={formProjects}
        onSaved={list.refresh}
        onCancel={handleCancelForm}
        isOpen={showForm}
      />

      <div className="flex items-center justify-end">
        <div className="flex items-center gap-2">
          <NewItemButton label="New" onClick={handleNewEntry} />
          <SearchButton onSearch={list.setSearchInput} placeholder="Search" />
          <ThreeDotMenuButton items={menuItems} />
        </div>
      </div>

      <GenericTable
        columns={columns}
        {...list.tableProps}
        rowKey={(row) => row.id}
        scrollContainerMaxWidth="calc(100vw - 19.2rem)"
        minTableWidth={1200}
        onRowClick={(entry) => { setEditingEntry(entry); setShowForm(true); }}
        selectable={true}
        selectedKeys={selectedRows}
        onSelectionChange={setSelectedRows}
        emptyState={list.error ? `Error: ${list.error}` : undefined}
      />
    </div>
  );
}
